import { useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  Ban,
  CheckCircle2,
  Copy,
  FileText,
  Layers,
  Link2,
  MailQuestion,
  Play,
  Scissors,
  ShieldQuestion,
  Ticket as TicketIcon,
} from 'lucide-react';
import { canAccess } from '../lib/access.js';
import { api } from '../lib/api.js';
import { ago } from '../lib/format.js';
import {
  AccessDenied,
  Badge,
  Button,
  Callout,
  ErrorState,
  Field,
  Input,
  PageHeader,
  Panel,
  Spinner,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  Textarea,
  cn,
} from '../ui/index.js';

interface Ctx {
  tenant: string;
  role: string | null;
  tenantName: string | undefined;
}

interface Ladder {
  settledAtRung: number;
  settledBy: string;
  requestType: string;
  systems: string[];
  strip: { originalLength: number; stripped: string; cutMarker: string | null };
  context: { projectNumbers: string[]; paths: string[]; deadlinePhrases: string[] };
  route: {
    queue: string | null;
    confidence: number | null;
    needsHumanRouting: boolean;
    tiebreakHint: string | null;
  };
  template: { label: string } | null;
}

type Disposition =
  | { kind: 'suppressed'; rule: string }
  | { kind: 'duplicate'; ticketId: string | null }
  | { kind: 'appended'; ticketId: string; seq: number }
  | { kind: 'created'; ticketId: string; number: number; ladder: Ladder };

const RUNGS = [
  'strip',
  'machine mail',
  'known template',
  'pattern rules',
  'systems',
  'working context',
  'route',
];

interface Sample {
  id: string;
  label: string;
  from: string;
  subject: string;
  body: string;
  note: string;
}

/**
 * The one sample that cannot come from the group's own tickets.
 *
 * The picker is otherwise filled from real messages the desk received — see
 * `/intake/samples`, which reads tickets. A suppressed message BY DEFINITION
 * never became a ticket, so the auto-reply case is unreachable from the
 * record however many of them arrived. It is kept hand-written because the
 * behaviour it demonstrates — the pipeline declining to open anything — is
 * the one the bench most needs to be able to show.
 */
const BUILT_IN: Sample[] = [
  {
    id: 'ooo',
    label: 'Our own acknowledgement, bounced',
    from: 'cchen@esassoc.com',
    subject: 'Automatic reply: [ESA-esatech-00042] We received your request',
    body: 'I am out of the office until Monday with limited access to email.',
    note: '120 of the 305 machine tickets in the record are exactly this — our acknowledgement hitting an out-of-office responder. It must never open a ticket, which is also why it is the one sample written by hand: there is no ticket to draw it from.',
  },
];

interface RealSample {
  number: number;
  subject: string;
  from: string;
  body: string;
  truncated: boolean;
}

/**
 * The intake TEST BENCH.
 *
 * Not a request form — /new is that. This replays a message through the real
 * ingest path (signature stripping, machine-mail suppression, threading,
 * routing) so the ladder can be watched deciding, and it leaves behind
 * whatever that path produces, including a real ticket.
 *
 * 95% of demand arrives as unstructured email, so this is the most important
 * pipeline in the system and the one worth being able to watch run.
 */
export function IntakePage() {
  const { tenant, role, tenantName } = useOutletContext<Ctx>();
  const allowed = canAccess(role, 'intake');
  // The suppressed-mail log is a report, so it needs a higher role than the
  // rest of this page. Gate the query rather than letting it 403.
  const canSeeSuppressed = canAccess(role, 'dashboard');

  const [active, setActive] = useState(BUILT_IN[0]!.id);
  const [from, setFrom] = useState(BUILT_IN[0]!.from);
  const [subject, setSubject] = useState(BUILT_IN[0]!.subject);
  const [body, setBody] = useState(BUILT_IN[0]!.body);

  /**
   * Real messages this group actually received, for the picker.
   *
   * The bench used to ship invented people, which made a live pipeline read
   * as a mock. These are the desk's own words.
   */
  const real = useQuery({
    queryKey: ['intake-samples', tenant],
    queryFn: () => api.get<{ samples: RealSample[] }>(`/api/v1/tenants/${tenant}/intake/samples`),
    enabled: Boolean(tenant) && allowed,
  });

  const samples: Sample[] = [
    ...BUILT_IN,
    ...(real.data?.samples ?? []).map((s) => ({
      id: `t${s.number}`,
      label: s.subject,
      from: s.from,
      subject: s.subject,
      body: s.body,
      note: `Ticket #${s.number} as it arrived${s.truncated ? ', shortened here for the bench' : ''}. Sending it runs the same message through the pipeline again and files a second ticket.`,
    })),
  ];

  const note = samples.find((s) => s.id === active)?.note;

  const send = useMutation({
    mutationFn: () =>
      api.post<{ disposition: Disposition }>(
        `/api/v1/tenants/${tenant}/intake/simulate`,
        { from, subject, body },
      ),
  });

  const suppressed = useQuery({
    queryKey: ['suppressed', tenant],
    queryFn: () =>
      api.get<{
        suppressed: { rule_label: string; from_email: string; subject: string; at: string }[];
      }>(`/api/v1/tenants/${tenant}/intake/suppressed`),
    enabled: Boolean(tenant) && allowed && canSeeSuppressed,
  });

  const load = (id: string) => {
    const s = samples.find((x) => x.id === id);
    if (!s) return;
    setActive(id);
    setFrom(s.from);
    setSubject(s.subject);
    setBody(s.body);
    send.reset();
  };

  // Hooks must run unconditionally, so the access check gates the RENDER,
  // never the hook order.
  if (!allowed) {
    return <AccessDenied area="intake" role={role} tenantName={tenantName} />;
  }

  return (
    <div className="mx-auto max-w-[1480px] p-4 sm:p-6">
      <PageHeader
        title="Intake test bench"
        description="Not the request form — this replays a message through the real email pipeline and shows which rung settled it. Unless the message is suppressed, it files a real ticket in this group."
      />

      <div className="grid gap-4 xl:grid-cols-2">
        <Panel
          title="Replay a message"
          subtitle={`The same code path as the mailbox — nothing here is mocked, and a message that is not suppressed becomes a real ticket in ${tenantName ?? 'this group'}`}
        >
          <div className="mb-3 flex flex-wrap items-center gap-1.5">
            {samples.map((s) => (
              <button
                key={s.id}
                onClick={() => load(s.id)}
                title={s.subject}
                className={cn(
                  'max-w-[20rem] truncate rounded-full px-2.5 py-1 text-2xs font-medium transition-colors',
                  active === s.id
                    ? 'bg-brand text-ink-on-brand'
                    : 'bg-surface text-ink-secondary ring-1 ring-inset ring-line hover:bg-surface-hover hover:text-ink',
                )}
              >
                {s.label}
              </button>
            ))}
            {real.isLoading && <Spinner />}
            {/*
              A group with no tickets yet has nothing to draw from. Say that,
              rather than rendering a picker with one entry and letting the
              reader wonder what happened to the rest.
            */}
            {real.isSuccess && real.data.samples.length === 0 && (
              <span className="text-2xs text-ink-tertiary">
                No tickets in this group to draw a real message from — type your own below.
              </span>
            )}
          </div>

          {note && (
            <Callout tone="brand" className="mb-3">
              {note}
            </Callout>
          )}

          <div className="space-y-3">
            <Field label="From">
              <Input value={from} onChange={(e) => setFrom(e.target.value)} />
            </Field>
            <Field label="Subject">
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
            </Field>
            <Field label="Body">
              <Textarea
                rows={9}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                className="font-mono text-xs"
              />
            </Field>
          </div>

          {send.error && (
            <div className="mt-3">
              <ErrorState error={send.error} />
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
            <Callout tone="warn" className="min-w-[16rem] flex-1">
              This is live. Anything that survives suppression is created as a real ticket in{' '}
              <strong>{tenantName ?? tenant}</strong>, with a number, an SLA clock and a queue.
            </Callout>
            <Button
              variant="primary"
              size="lg"
              leading={<Play className="h-3.5 w-3.5" />}
              loading={send.isPending}
              onClick={() => send.mutate()}
            >
              Run the pipeline
            </Button>
          </div>
        </Panel>

        <Panel title="What the ladder decided">
          {send.isPending && <Spinner label="Running the ladder" />}
          {!send.data && !send.isPending && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <MailQuestion aria-hidden className="h-6 w-6 text-ink-tertiary" />
              <p className="mt-3 text-sm text-ink-secondary">
                Send a message to see the result.
              </p>
              <p className="mt-1 max-w-xs text-2xs text-ink-tertiary">
                Rungs one to three are deterministic and never call a model.
              </p>
            </div>
          )}
          {send.data && <Result d={send.data.disposition} tenant={tenant} />}
        </Panel>

        {canSeeSuppressed && <Panel
          title="Suppressed mail"
          subtitle="Machine mail is logged with the rule that caught it, never silently dropped"
          className="xl:col-span-2"
          flush
        >
          {suppressed.isLoading && <Spinner />}
          {suppressed.data && suppressed.data.suppressed.length === 0 && (
            <p className="py-8 text-center text-xs text-ink-tertiary">Nothing suppressed yet.</p>
          )}
          {suppressed.data && suppressed.data.suppressed.length > 0 && (
            <Table minWidth={720} density="compact">
              <THead>
                <TR>
                  <TH width={240}>Rule</TH>
                  <TH width={220}>From</TH>
                  <TH>Subject</TH>
                  <TH width={90} numeric>
                    When
                  </TH>
                </TR>
              </THead>
              <TBody>
                {suppressed.data.suppressed.slice(0, 25).map((s, i) => (
                  <TR key={i}>
                    <TD>
                      <Badge tone="warn" icon={<Ban />}>
                        {s.rule_label}
                      </Badge>
                    </TD>
                    <TD muted className="text-xs">
                      {s.from_email}
                    </TD>
                    <TD className="text-xs">
                      <span className="line-clamp-1">{s.subject}</span>
                    </TD>
                    <TD numeric className="whitespace-nowrap text-xs text-ink-tertiary">
                      {ago(s.at)}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </Panel>}
      </div>
    </div>
  );
}

function Result({ d, tenant }: { d: Disposition; tenant: string }) {
  if (d.kind === 'suppressed') {
    return (
      <div>
        <div className="mb-3 flex items-center gap-2">
          <Badge tone="warn" size="md" icon={<Ban />}>
            Suppressed at rung 2 — no ticket created
          </Badge>
        </div>
        <p className="text-sm text-ink">
          Matched <strong>{d.rule}</strong>.
        </p>
        <Callout tone="neutral" className="mt-3">
          This message needed no human and never reached a model. It is recorded in the suppressed
          log below, so the decision is auditable rather than invisible.
        </Callout>
      </div>
    );
  }

  if (d.kind === 'duplicate') {
    return (
      <Callout tone="info" icon={<Copy />} title="Already ingested">
        This Message-ID has been seen, so nothing was created. Replaying the whole mailbox is a
        no-op.
      </Callout>
    );
  }

  if (d.kind === 'appended') {
    return (
      <Callout tone="info" icon={<Link2 />} title={`Threaded on as message #${d.seq}`}>
        Matched by RFC-5322 headers before any subject heuristic — which is what stops a forwarded
        chain from landing on the wrong ticket.
      </Callout>
    );
  }

  const l = d.ladder;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Link to={`/t/${tenant}/${d.number}`}>
          <Badge tone="ok" size="md" icon={<CheckCircle2 />}>
            Created #{d.number}
          </Badge>
        </Link>
        <ArrowRight aria-hidden className="h-3 w-3 text-ink-tertiary" />
        <Badge tone="brand" size="md">
          settled at rung {l.settledAtRung} — {RUNGS[l.settledAtRung - 1]}
        </Badge>
      </div>

      {/* The rung ladder, with everything above the settling rung dimmed. */}
      <ol className="flex gap-1">
        {RUNGS.map((r, i) => {
          const n = i + 1;
          const reached = n <= l.settledAtRung;
          return (
            <li key={r} className="flex-1">
              <div
                className={cn(
                  'h-1 rounded-full',
                  n === l.settledAtRung
                    ? 'bg-brand'
                    : reached
                      ? 'bg-brand/35'
                      : 'bg-surface-sunken',
                )}
              />
              <p
                className={cn(
                  'mt-1 truncate text-3xs',
                  n === l.settledAtRung ? 'font-semibold text-brand' : 'text-ink-tertiary',
                )}
              >
                {n}. {r}
              </p>
            </li>
          );
        })}
      </ol>

      <dl className="divide-y divide-line-subtle text-xs">
        <Row label="Matched">{l.settledBy}</Row>
        <Row label="Request type">
          <strong className="text-ink">{l.requestType}</strong>
        </Row>
        {l.template && (
          <Row label="Template">
            <Badge tone="info" icon={<FileText />}>
              {l.template.label}
            </Badge>
          </Row>
        )}
        <Row label="Queue">
          {l.route.queue ?? '—'}
          {l.route.confidence !== null && (
            <span className="ml-1.5 text-ink-tertiary">
              {Math.round(l.route.confidence * 100)}% measured purity
            </span>
          )}
        </Row>
        {l.route.needsHumanRouting && (
          <Row label="Routing">
            <Badge tone="warn" icon={<ShieldQuestion />}>
              Flagged for a person
              {l.route.tiebreakHint && ` — tie-break on ${l.route.tiebreakHint.replace('_', ' ')}`}
            </Badge>
          </Row>
        )}
        <Row label="Systems">
          {l.systems.length > 0 ? (
            <span className="flex flex-wrap gap-1">
              {l.systems.map((s) => (
                <Badge key={s} tone="neutral" icon={<Layers />}>
                  {s}
                </Badge>
              ))}
            </span>
          ) : (
            '—'
          )}
        </Row>
        {l.context.projectNumbers.length > 0 && (
          <Row label="Project">
            <span className="nums font-mono">{l.context.projectNumbers.join(', ')}</span>
          </Row>
        )}
        {l.context.paths.length > 0 && (
          <Row label="Paths">
            <span className="break-all font-mono text-2xs">{l.context.paths.join(', ')}</span>
          </Row>
        )}
        {l.context.deadlinePhrases.length > 0 && (
          <Row label="Deadline said">{l.context.deadlinePhrases.join(', ')}</Row>
        )}
        <Row label="Stripped">
          <span className="inline-flex items-center gap-1.5">
            <Scissors aria-hidden className="h-3 w-3 text-ink-tertiary" />
            <span className="nums">
              {l.strip.originalLength} → {l.strip.stripped.length} chars
            </span>
            {l.strip.cutMarker && (
              <Badge tone="neutral">cut at {l.strip.cutMarker}</Badge>
            )}
          </span>
        </Row>
      </dl>

      <div>
        <p className="mb-1.5 text-2xs font-semibold uppercase tracking-wide text-ink-tertiary">
          What the rules actually read
        </p>
        <pre className="overflow-x-auto whitespace-pre-wrap rounded-md border border-line bg-surface-sunken p-3 font-mono text-2xs leading-relaxed text-ink">
          {l.strip.stripped}
        </pre>
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[6.5rem_minmax(0,1fr)] items-baseline gap-3 py-2 first:pt-0">
      <dt className="text-2xs uppercase tracking-wide text-ink-tertiary">{label}</dt>
      <dd className="min-w-0 text-ink">{children}</dd>
    </div>
  );
}
